import Box from "@/common/components/Box";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import SmallBadge from "@/common/components/SmallBadge";
import { cn } from "@/common/utils/cn";
import { auctionProposalLeadValueByDirectiveEntryKlass } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionProposalLeadValue.constant";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/types/updatedSummary.type";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type";
import { format } from "date-fns";
import { CalendarCheck2Icon, CheckIcon, ClockIcon, CrownIcon, WrenchIcon, XIcon } from "lucide-react";
import { Tooltip } from "react-tooltip";

interface AuctionProposalProps {
  proposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData;
  directiveEntryType?: MaintenanceForwardingsDirectiveEntryKlass;
  onAccept?: () => void;
  onDisqualify?: () => void;
}

const AuctionProposal = ({
  proposal,
  directiveEntryType,
  onAccept,
  onDisqualify,
}: AuctionProposalProps) => {
  const isFirstPosition = proposal.position === 1;

  const LeadValue = directiveEntryType
    ? auctionProposalLeadValueByDirectiveEntryKlass[directiveEntryType]
    : undefined;

  const tooltipId = `auction-proposal-${proposal.id}`;

  return (
    <>
      <Box
        className={cn(
          'px-2.5 py-3 rounded-lg flex items-center justify-between gap-2 min-h-12',
        )}
      >
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1">
            {isFirstPosition && (
              <CrownIcon
                size={16}
                strokeWidth={1.5}
                className="text-warning-secondary"
              />
            )}

            <p className={cn('text-sm font-medium leading-none', isFirstPosition && 'text-yellow-400')}>
              {proposal.position}º
            </p>
          </div>

          <ImageWithDisplayName
            placeholderIcon={WrenchIcon}
            displayName={proposal.establishment_fantasy_name}
            displayNameClassName="text-sm"
            imageClassName="size-5"
            containerClassName="gap-1.5"
            iconClassName="size-3.5"
          />
        </div>

        <div className="flex items-center gap-1">
          {LeadValue && (
            <LeadValue proposal={proposal} tooltipId={tooltipId} />
          )}

          {proposal.contents?.deadline_to_complete_in_days != null && (
            <SmallBadge
              data-tooltip-id={tooltipId}
              data-tooltip-content="Prazo em dias"
              LeftIcon={ClockIcon}
              text={`${proposal.contents?.deadline_to_complete_in_days} Dias`}
              className="bg-white-200"
            />
          )}

          {proposal.contents?.start_at && (
            <SmallBadge
              data-tooltip-id={tooltipId}
              data-tooltip-content="Data de início"
              LeftIcon={CalendarCheck2Icon}
              text={format(new Date(proposal.contents?.start_at), 'dd/MM HH:mm')}
              className="bg-white-200"
            />
          )}

          {onAccept && (
            <SmallBadge
              data-tooltip-id={tooltipId}
              data-tooltip-content="Aceitar proposta"
              LeftIcon={CheckIcon}
              className="bg-success hover:bg-success-hover transition-colors cursor-pointer text-white"
              onClick={onAccept}
            />
          )}

          {onDisqualify && (
            <SmallBadge
              data-tooltip-id={tooltipId}
              data-tooltip-content="Desclassificar proposta"
              LeftIcon={XIcon}
              className="bg-danger hover:bg-[#E03E1AAA] transition-colors cursor-pointer text-white"
              onClick={onDisqualify}
            />
          )}
        </div>
      </Box>

      <Tooltip id={tooltipId} className="z-50" />
    </>
  );
}

export default AuctionProposal;
